import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen py-12 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            Last updated: August 5, 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">1. Data Controller</h2>
            <p className="text-muted-foreground leading-relaxed">
              JobMatch Pro (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is the data controller responsible for your personal data. 
              We are committed to protecting your privacy and ensuring compliance with the General Data Protection 
              Regulation (GDPR) and other applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">2. Data We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect only the data strictly necessary for job matching services:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Contact Information:</strong> Name and email address</li>
              <li><strong>CV/Resume:</strong> Your uploaded document (PDF or DOCX format)</li>
              <li><strong>Job Preferences:</strong> Location preferences and target roles</li>
              <li><strong>Submission Metadata:</strong> Date and time of submission</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>Important:</strong> We do not intentionally collect or process &ldquo;Special Category Data&rdquo; 
              (e.g., health information, religious beliefs, ethnic origin) from your CV. If such data is 
              inadvertently included in your CV, it will not be used for matching purposes.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">3. Purpose of Processing</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We process your personal data for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>To match your profile with relevant job opportunities</li>
              <li>To show job-match results and application materials inside your account</li>
              <li>To improve our matching algorithms and services</li>
            </ul>
            
            <div className="mt-6 p-4 border border-border bg-surface">
              <h3 className="font-medium mb-2">AI & Automated Processing Disclosure</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                We use AI-driven algorithms to analyze your CV and preferences to suggest job matches. 
                These AI-generated suggestions are <strong>for informational purposes only</strong> and do not 
                constitute a hiring decision. Final hiring decisions are always made by human recruiters 
                at the respective companies. You have the right to request human review of any automated 
                assessment under GDPR Article 22.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">4. Legal Basis</h2>
            <p className="text-muted-foreground leading-relaxed">
              We process your data based on your <strong>explicit consent</strong> (GDPR Article 6(1)(a)), 
              which you provide when checking the consent box on our submission form. 
              You may withdraw your consent at any time—see Section 7 for details.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">5. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your personal data and CV are retained while your account and active job-search records are needed 
              to provide the service. You may request deletion at any time through the data management page.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">6. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Encryption in Transit:</strong> All data is transmitted using TLS/SSL encryption (HTTPS)</li>
              <li><strong>Encryption at Rest:</strong> CV files and database records are stored with encryption at rest</li>
              <li><strong>Access Control:</strong> Access to personal data is restricted to authorized personnel only</li>
              <li><strong>No Model Training:</strong> Your CV and personal data are <strong>never used to train AI models</strong></li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">7. Your Rights (GDPR)</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Under GDPR, you have the following rights:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Right of Access (Art. 15):</strong> Request a copy of all data we hold about you</li>
              <li><strong>Right to Rectification (Art. 16):</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure (Art. 17):</strong> Request deletion of your data (&ldquo;Right to be Forgotten&rdquo;)</li>
              <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a machine-readable format (JSON)</li>
              <li><strong>Right to Withdraw Consent (Art. 7):</strong> Withdraw your consent at any time</li>
              <li><strong>Right to Object to Automated Processing (Art. 22):</strong> Request human review of automated decisions</li>
            </ul>
            
            <div className="mt-6 p-4 border border-border bg-surface">
              <h3 className="font-medium mb-2">How to Exercise Your Rights</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                You can exercise your rights through our self-service{' '}
                <Link to="/manage-data" className="text-foreground underline hover:no-underline">
                  Data Management Page
                </Link>, where signed-in users can view, export, or request deletion of their data.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">8. Data Sharing & Sub-processors</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We do not sell your personal data. Your data may be processed by the following categories of service providers:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>
                <strong>Cloud Storage Providers:</strong> For secure file storage (CVs)
              </li>
              <li>
                <strong>AI Model Providers:</strong> We use third-party AI services to analyze CV content 
                for job matching. Your data is processed via API calls and is <strong>not used to train 
                their foundational models</strong>. Data is processed in accordance with their enterprise 
                privacy agreements.
              </li>
              <li>
                <strong>Database Services:</strong> For storing your profile information
              </li>
              <li>
                <strong>Potential Employers:</strong> Only with your explicit, per-application consent
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              All sub-processors are bound by Data Processing Agreements (DPAs) that ensure GDPR-compliant handling of your data.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">9. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your data may be processed on servers located in the <strong>European Union</strong> and/or the 
              <strong> United States</strong>. For any transfers outside the EU/EEA, we ensure compliance through 
              approved safeguards including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>EU-U.S. Data Privacy Framework certification of providers</li>
              <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">10. Human Oversight</h2>
            <p className="text-muted-foreground leading-relaxed">
              While our job matching process uses AI automation, please note:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Job match suggestions are generated by AI and are recommendations only</li>
              <li>No hiring decisions are made automatically by our system</li>
              <li>Hiring companies remain responsible for their own employment decisions</li>
              <li>You may request human review of how your profile was processed at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">11. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use only essential cookies necessary for the functioning of our service (session management, 
              theme preferences). We do not use tracking, analytics, or advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">12. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Material changes will be communicated 
              in the product or through another appropriate channel. The &ldquo;Last updated&rdquo; date at the top indicates the 
              most recent revision.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl font-medium mb-4">13. Contact & Complaints</h2>
            <p className="text-muted-foreground leading-relaxed">
              For privacy-related questions, to exercise your data rights, or to lodge a complaint, use the Data Management Page or the support channel provided with your account.
            </p>
            <div className="mt-4 p-4 border border-border bg-surface">
              <p className="text-muted-foreground text-sm">
                You also have the right to lodge a complaint with your local Data Protection Authority (DPA). 
                For EU residents, you can find your national DPA at{' '}
                <a 
                  href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-foreground underline hover:no-underline"
                >
                  edpb.europa.eu
                </a>.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
